import { CreateStatusPageDTO, StatusPageDTO, UpdateStatusPageDTO } from '@meshwatch/types';

import { DynamoDBClient } from '../../shared';
import { DEFAULT_DYNAMO_DB_CLIENT } from '../../shared/aws/dynamodb';
import { ForbiddenException, validationBoom } from '../../shared/rest';
import BaseService, { ServiceResponse } from '../../shared/services';
import Authorization from '../../shared/services/access';
import { FormErrorsBoom } from '../../shared/validation/types';
import { DynamoStatusPageDatasource, StatusPageDatasource } from '../db/datasource';
import { validateCreateStatusPagePayload, validateUpdateStatusPagePayload } from '../validation';

export class StatusPageService extends BaseService {
  private readonly datasource: StatusPageDatasource;

  public constructor(dynamoClient: DynamoDBClient = DEFAULT_DYNAMO_DB_CLIENT) {
    super();
    this.datasource = new DynamoStatusPageDatasource(dynamoClient);
  }

  public getStatusPage = async (
    id: string
  ): Promise<ServiceResponse<StatusPageDTO | FormErrorsBoom<undefined>>> => {
    return this.tryExecute(async () => {
      const statusPage = await this.datasource.getStatusPage(id);
      return this.serviceResponse(statusPage);
    });
  };

  public lookupStatusPage = async (
    customDomain: string
  ): Promise<ServiceResponse<StatusPageDTO | FormErrorsBoom<undefined>>> => {
    return this.tryExecute(async () => {
      const statusPage = await this.datasource.lookupStatusPage(customDomain);
      return this.serviceResponse(statusPage);
    });
  };

  public updateStatusPage = async (
    userId: string,
    updateStatusPagePayload: UpdateStatusPageDTO
  ): Promise<ServiceResponse<StatusPageDTO | FormErrorsBoom<undefined>>> => {
    const validationResponse = await validateUpdateStatusPagePayload(updateStatusPagePayload);
    if (validationResponse.error) {
      return this.serviceResponseFromBoom(validationBoom(validationResponse.errors));
    }

    return this.tryExecute(async () => {
      const existingStatusPage = await this.datasource.getStatusPage(updateStatusPagePayload.id);

      if (Authorization.of(userId).cannotAccessEntity(existingStatusPage)) {
        throw new ForbiddenException('You are not authorized to perform this action');
      }

      const updatePayload: StatusPageDTO = {
        ...updateStatusPagePayload,
        created: existingStatusPage.created,
      };

      const updatedStatusPage = await this.datasource.updateStatusPage(updatePayload);
      return this.serviceResponse(updatedStatusPage);
    });
  };

  public deleteStatusPage = async (
    userId: string,
    id: string
  ): Promise<ServiceResponse<StatusPageDTO | FormErrorsBoom<undefined>>> => {
    return this.tryExecute(async () => {
      const statusPage = await this.datasource.getStatusPage(id);
      if (Authorization.of(userId).cannotAccessEntity(statusPage)) {
        throw new ForbiddenException('You are not authorized to perform this action');
      }

      await this.datasource.deleteStatusPage(statusPage.id);
      return this.serviceResponse(statusPage);
    });
  };

  public createStatusPage = async (userId: string, params: CreateStatusPageDTO) => {
    const validationResponse = await validateCreateStatusPagePayload(params);
    if (validationResponse.error) {
      return this.serviceResponseFromBoom(validationBoom(validationResponse.errors));
    }

    return this.tryExecute(async () => {
      if (Authorization.of(userId).cannotAccessEntity(params)) {
        throw new ForbiddenException('You are not authorized to perform this action');
      }
      const statusPage = await this.datasource.createStatusPage(params);
      return { statusCode: 201, body: statusPage };
    });
  };

  public findStatusPages = async (userId: string) => {
    return this.tryExecute(async () => {
      const statusPages = await this.datasource.findStatusPages(userId);
      return { statusCode: 200, body: statusPages };
    });
  };
}

const statusPageService = new StatusPageService();

export default statusPageService;
